/**
 * This Object is a mixture of a Map but with array operations (like .find, .filter, .map and .sort).
 */
import util from './util';
import upFirstLetter from "./picos/upFirstLetter";
import {genUUIDBase62} from "./picos/uuids";
import {EntityMapper, EntityType} from "../@types/entity";
import {SchemaIndex, SchemaMap, KeysFn, PresentFn, uuid} from "../@types/schema";

const logging = false;

/**
 * Static caches keyed by each ArrayMap instance's own _map reference.
 * Each clone gets its own _map, so caches are per-instance.
 */
const _valuesCache = new WeakMap<Map<string, any>, any[]>();
const _indexCache = new WeakMap<Map<string, any>, Map<string, any[]>>();
const _mappingCache = new WeakMap<Map<string, any>, Map<string, any[]>>();

class ArrayMap<T extends EntityType> {
    // Entity type, e.g. User or Account
    private _type: string;

    // Unique ID for this ArrayMap (UUID62)
    private readonly _id: string;

    // Internal map of UUID to EntityType
    private _map: Map<string, T>;

    // Internal map of index names to EntityMaps
    private _indexes: Map<string, Map<string, T>>;

    // Internal map of index names to index functions
    private _indexFunctions: Map<string, (element: T) => boolean>;

    // Internal map of map name to map of EntityType
    private _mappings: Map<string, Map<any, Map<string, T>>>;

    // Internal map of map name to mapping functions object
    private _mappingFunctions: Map<string, { keysFn: KeysFn; presentFn: PresentFn }>;

    // Maximum addedOn date in this ArrayMap
    private _maxAddedOn: number;

    // Maximum dbAddedOn date in this ArrayMap
    private _maxDbAddedOn: number;

    /**
     * Constructor for ArrayMap
     *
     * @param {string} type - Entity type (e.g., 'ActivityCategory')
     * @param {<T>[]} [data] - Optional initial data
     * @param {EntitySchema} [schema] - Optional schema defining indexes and mappings
     */
    constructor(type: string, data: T[] = [], schema?: any) {
        /**
         * Unique ID for this map (used for debugging).
         *
         * @type {string}
         * @private
         */
        this._id = genUUIDBase62();

        /**
         * The internal mapping of IDs to Classes.
         * @type {Map}
         * @private
         */
        this._map = new Map();

        /**
         * An ArrayMap of indexes.
         *
         * @type {Map}
         * @private
         */
        this._indexes = new Map();

        /**
         * Map of index names to filter functions.
         *
         * @type {Map}
         * @private
         */
        this._indexFunctions = new Map();

        /**
         * Map of submaps linked to a particular field value.
         *
         * @type {Map}
         * @private
         */
        this._mappings = new Map();

        /**
         * Map of mapping names to mapping functions.
         *
         * @type {Map}
         * @private
         */
        this._mappingFunctions = new Map();

        /**
         * The most recent (highest) addedOn date (saved locally)
         */
        this._maxAddedOn = 0;

        /**
         * The most recent (highest) dbAddedOn date (saved to database)
         */
        this._maxDbAddedOn = 0;

        /**
         * The type of object being stored (string).
         *
         * @type {string}
         * @private
         */
        this._type = type || '';

        // Check for nulls, just use an empty array
        if (!data) data = [];

        // If we got a non-null, non-array data argument, complain and use defaults.
        if (data && !(data instanceof ArrayMap) && !Array.isArray(data)) {
            console.error('Data passed to ArrayMap is not an array', data);
            data = [];
        }

        // Create the internal map and fill it with passed data
        this._map = new Map();
        data.forEach(d => this.push(d));

        // Initialize indexes and mappings from schema if provided
        if (schema) {
            (schema.indexes as SchemaIndex[])?.forEach(index => {
                this.createIndex(index.name, index.fn)
            });
            (schema.maps as SchemaMap[])?.forEach(map => this.createMap(map.name, map.keysFn, map.presentFn));
        }
    }

    // ==================== Cache Management ====================

    /**
     * Invalidate all cached values (values, indexes, mappings).
     * Called when data in _map actually changes.
     */
    private _invalidateCaches = (): void => {
        _valuesCache.delete(this._map);
        _indexCache.delete(this._map);
        _mappingCache.delete(this._map);
    }

    // ==================== Core Methods ====================

    /**
     * Map-like clear method.
     */
    clear = () => {
        // Clear the map of values
        this._map.clear();

        // Clear calculated variables
        this._maxAddedOn = 0;
        this._maxDbAddedOn = 0;

        // Clear indexes
        for (let o of this._indexes.values()) o.clear();

        // Clear mappings
        for (let o of this._mappings.values()) o.clear();

        // Invalidate caches
        this._invalidateCaches();
    }

    /**
     * Return a new ArrayMap with this class's map entries.
     *
     * @return {ArrayMap}
     */
    clone = (): ArrayMap<T> => {
        const result = new ArrayMap<T>(this.type);

        // Independent copy of the data map
        result._map = new Map(this._map);

        // Copy scalar values
        result._maxAddedOn = this._maxAddedOn;
        result._maxDbAddedOn = this._maxDbAddedOn;

        // Deep-copy indexes (Map<string, Map<string, T>>)
        result._indexes = new Map();
        for (const [name, indexMap] of this._indexes) {
            result._indexes.set(name, new Map(indexMap));
        }
        result._indexFunctions = this._indexFunctions; // immutable refs, safe to share

        // Deep-copy mappings (Map<string, Map<any, Map<string, T>>>)
        result._mappings = new Map();
        for (const [name, outerMap] of this._mappings) {
            const newOuter = new Map();
            for (const [key, innerMap] of outerMap) {
                newOuter.set(key, new Map(innerMap));
            }
            result._mappings.set(name, newOuter);
        }
        result._mappingFunctions = this._mappingFunctions; // immutable refs, safe to share

        return result;
    }

    /**
     * Array-like concat method. USE WITH CARE!
     */
    concat = (elements: T[]) => {
        // That is terrible.  Just set the elements.
        // return Array.from(this._map.values()).concat(elements);
        for (const e of elements) this.set(e);
        return this.values();
    }

    /**
     * Using the given filter, create a named index of items.
     *
     * @param {string} name
     * @param {(element: <T>) => boolean} indexFilter
     */
    createIndex = (name: string, indexFilter: (element: T) => boolean) => {
        // Create a new Map
        const index = new Map();

        // Fill the index with any entries that currently fit it.
        for (const o of this._map.values()) {
            if (indexFilter(o)) index.set(o.id, o);
        }

        // Add to the list of indexes (will overwrite if required)
        this._indexes.set(name, index);
        this._indexFunctions.set(name, indexFilter);
    }

    /**
     * Helper method to update values in a mapping.
     *
     * @param {Map} mapping - the mapping to update
     * @param {any} key - a key to update
     * @param {object} element - the element to set with the key
     */
    _updateMapping = (mapping: Map<any, Map<string, T>>, key: any, element: T) => {
        // If this already exists, update it's mapping
        if (mapping.has(key)) mapping.get(key)?.set(element.id, element);
        // Otherwise, create a new map and add as the first value
        else {
            const m = new Map();
            m.set(element.id, element);
            mapping.set(key, m);
        }
    }

    /**
     * Create submapped indexes. For example, we might want an index of tasks matching a given invoiceId.
     * We would create a mapping called invoicedId in this._mappings, add a key of invoiceId, then set its
     * value to a map of tasks that have this invoiceId.
     *
     * These can be more complicated. For example, if we want an index of matterIds to tasks, we would need
     * to parse tieIns.id for a match with the given matterId.  Since there can be multiple values for tieIns
     * (one task can be related to multiple matters), we need to have a function to tell us
     * 1. All the values in the element matching a given criteria
     * 2. A function to test if a given value is present in the values
     *
     * @param {string} name - the name of the mapping (does not need to be identical to the field being indexed)
     * @param {function} keysFn - a function to get all values for the given mapping in an element
     * @param {function} presentFn - a function to check if a value is present in a given mapping for a given key
     */
    createMap = (name: string, keysFn: KeysFn, presentFn: PresentFn) => {
        const mapping = new Map();

        // Iterate through existing entries, and create submaps for each key present.
        for (const o of this._map.values()) {
            // Only work with non-deleted entities in a mapping
            if (o.status !== 'D') {
                // Get the distinct key(s) present in this element
                const distinctValues = keysFn(o);

                if (Array.isArray(distinctValues)) {
                    distinctValues.forEach(key => this._updateMapping(mapping, key, o));
                }
                else this._updateMapping(mapping, distinctValues, o);
            }
        }

        this._mappings.set(name, mapping);
        this._mappingFunctions.set(name, {keysFn, presentFn});
    }

    /**
     * Map-like delete function. Takes an entity with an id field as the key.
     *
     * @param {<T>} element
     * @param {uuid} element.id
     * @param {string} element.status
     */
    delete = (element: T) => {
        // Invalidate caches since we're removing an entity
        this._invalidateCaches();

        this._map.delete(element.id);

        // Update calculated variables
        if (element.addedOn === this._maxAddedOn || element.dbAddedOn === this._maxDbAddedOn) {
            this._maxAddedOn = 0;
            this._maxDbAddedOn = 0;

            // Recalculate the variables
            this.forEach(v => {
                if (v.addedOn > this._maxAddedOn) this._maxAddedOn = v.addedOn;
                if (v.dbAddedOn > this._maxDbAddedOn) this._maxDbAddedOn = v.dbAddedOn;
            });
        }

        // Delete from any indexes
        for (let k of this._indexes.keys()) {
            this._indexes.get(k)?.delete(element.id);
        }

        // Delete from any mappings
        for (let fieldName of this._mappings.keys()) {
            const mapping = this._mappings.get(fieldName);
            if (mapping) for (let k of mapping.keys()) mapping.get(k)?.delete(element.id);
        }
    }

    /**
     * Array-like every method.
     *
     * @param {Function} fn
     * @returns {boolean}
     */
    every = (fn: (o: T) => boolean): boolean => {
        for (const o of this._map.values()) {
            if (!fn(o)) return false;
        }

        return true;
    }

    /**
     * Array-like find method.
     */
    find = (fn: (o: T) => boolean): T | null => {
        for (const o of this._map.values()) {
            if (fn(o)) return o as T;
        }

        return null;
    }

    /**
     * Array-like filter method.
     */
    filter = (fn: (o: T, i?: number, arr?: T[]) =>
        boolean): T[] => {
        const result = [];
        let i = 0;

        // Check if function uses index/array parameters
        const needsContext = fn.length > 1;
        const arr = needsContext ? this.values() :
                    undefined;

        for (const o of this._map.values()) {
            if (needsContext ? fn(o, i++, arr!) :
                fn(o)) {
                result.push(o);
            }
        }

        return result;
    }

    /**
     * Array-like forEach method.
     */
    forEach = (fn: (o: T) => void): void => {
        for (const o of this._map.values()) {
            fn(o);
        }
    }

    /**
     * Map-like get method.
     *
     * @param {uuid} id
     * @param {boolean} [noDeleted] - if true, return null if the item is deleted
     * @returns {<T>}
     */
    get = (id: uuid, noDeleted?: boolean): T | null => {
        const item = this._map.get(id);
        if (item && noDeleted && item.status === 'D') return null;
        else return item as T;
    }

    /**
     * Get a named index of this ArrayMap. Memoized to return the same array
     * reference if the underlying data hasn't changed.
     *
     * @param {string} name
     * @returns {<T>[]}
     */
    getIndex = (name: string): T[] => {
        const result = this._indexes.get(name);

        if (result) {
            // Check for cached index array
            let indexCacheMap = _indexCache.get(this._map);
            if (indexCacheMap) {
                const cached = indexCacheMap.get(name);
                if (cached) return cached as T[];
            } else {
                indexCacheMap = new Map();
                _indexCache.set(this._map, indexCacheMap);
            }

            // Build and cache the array
            const values: T[] = [];
            result.forEach(o => {
                if (o) values.push(o as T);
            })
            if (logging) console.debug(`i ${this._type} ${name} ${values.length}`);

            indexCacheMap.set(name, values);
            return values;
        }
        else {
            // This happens when a call is made for an index before data has been restored or fetched.
            console.error(`No index ${name} found for ${this._type}`);

            return [];
        }
    }

    /**
     * Get values for the given mapping and key. Memoized to return the same
     * array reference if the underlying data hasn't changed.
     *
     * @param {string|uuid} name
     * @param {string|uuid} key
     * @returns {<T>[]}
     */
    getMappingFor = (name: string | uuid, key: string | uuid): T[] => {
        const mapping = this.getMappingAsMap(name)?.get(key);

        if (!mapping) {
            if (logging) console.warn(`m ${this._type} ${name} E`);
            return [];
        }

        // Check for cached mapping array
        const cacheKey = `${name}:${key}`;
        let mappingCacheMap = _mappingCache.get(this._map);
        if (mappingCacheMap) {
            const cached = mappingCacheMap.get(cacheKey);
            if (cached) return cached as T[];
        } else {
            mappingCacheMap = new Map();
            _mappingCache.set(this._map, mappingCacheMap);
        }

        // Build and cache the array
        const result: T[] = [];
        mapping.forEach(ko => {
            if (ko) result.push(ko as T);
        });

        if (logging) console.debug(`m ${this._type} ${name} ${result.length}`);

        mappingCacheMap.set(cacheKey, result);
        return result;
    }

    /**
     * Get mapping for multiple keys at once.
     *
     * @param {string} name - the name of the mapping
     * @param {string[]|uuid[]} keys - an array of string values to use as keys.
     * @returns {<T>[]}
     */
    getMappingsFor = (name: string, keys: string[] | uuid[]): T[] => {
        if (!keys) return [];

        return util.flatten(keys.map(key => this.getMappingFor(name, key)));
    }

    /**
     * Get a named mapping as a map.
     *
     * @param {string} name - the name of the mapping
     * @returns {Map<any, Map<string, <T>>>|undefined}
     */
    getMappingAsMap = (name: string): Map<any, Map<string, T>> | undefined => {
        return this._mappings.get(name);
    }

    /**
     * Map-like get method
     * @param {uuid|<T>} idOrElement
     * @returns {boolean}
     */
    has = (idOrElement: string | T): boolean => {
        if (typeof idOrElement === 'string') return this._map.has(idOrElement);
        else return this._map.has(idOrElement.id);
    }

    /**
     * Array-like slice method. We can't do this because this is a Map, not an Array.
     */
    slice = (start: number, stop: number) => {
        return Array.from(this._map.values()).slice(start, stop);
    }

    /**
     * Get the unique ID of this map.
     *
     * @returns {string}
     */
    get id(): string {
        return this._id;
    }

    /**
     * Get indexes on this ArrayMap.
     *
     * @returns {Map}
     */
    get indexes(): Map<string, Map<string, T>> {
        return this._indexes;
    }

    /**
     * Array-like length attribute.  Returns the number of elements in the Map.
     *
     * @returns {number}
     */
    get length(): number {
        return this._map.size;
    };

    /**
     * Array-like map function.
     */
    map = (fn: (o: T, index: number, value: IterableIterator<T>) => any) => {
        const result = [];
        let index = 0;

        for (const o of this._map.values()) {
            result.push(fn(o, index++, this._map.values()));
        }

        return result;
    }


    push = (element: T): number => {
        // Invalidate caches since we're adding an entity
        this._invalidateCaches();

        this._map.set(element.id, element);

        // Add to indexes (if passes index function)
        for (let key of this._indexes.keys()) {
            if (this._indexFunctions.get(key)?.(element)) this._indexes.get(key)?.set(element.id, element);
        }

        // Add to mappings (if value present and not deleted)
        if (element.status !== 'D') {
            // Update calculated variables
            if (element.addedOn > this._maxAddedOn) this._maxAddedOn = element.addedOn;
            if (element.dbAddedOn > this._maxDbAddedOn) this._maxDbAddedOn = element.dbAddedOn;

            // Start by getting the field names for each of the mappings
            for (let fieldName of this._mappings.keys()) {

                // Get the distinct key(s) present in this element
                const {keysFn} = this._mappingFunctions.get(fieldName) || {};
                if (!keysFn) {
                    console.error(`No keysFn for ${fieldName} in ArrayMap<${this._type}>`);
                    continue;
                }

                const distinctValues = keysFn(element);
                const mapping = this._mappings.get(fieldName);

                if (!mapping) {
                    console.error(`No mapping for ${fieldName} in ArrayMap<${this._type}>`);
                    continue;
                }

                if (Array.isArray(distinctValues)) {
                    distinctValues.forEach(key => this._updateMapping(mapping, key, element));
                }
                else this._updateMapping(mapping, distinctValues, element);
            }
        }

        return this.length;
    }

    /**
     * Map-like set method. Since we know this element has an ID field, no need to specify the key.
     *
     * @param {<T>} element
     * @param {uuid} element.id
     * @param {string} element.status
     */
    set = (element: T) => {
        const oldValue = this._map.get(element.id);

        // Always invalidate caches when setting an entity.
        // Smart timestamp comparison doesn't work reliably because:
        // 1. The same update may be dispatched multiple times
        // 2. After first dispatch updates _map, subsequent dispatches see same timestamps
        // 3. The cache needs to be invalidated so values() rebuilds with new entity references
        this._invalidateCaches();

        const oldValueClone = oldValue?.clone();
        this._map.set(element.id, element);

        // Update calculated variables
        if (element.addedOn > this._maxAddedOn) this._maxAddedOn = element.addedOn;
        if (element.dbAddedOn > this._maxDbAddedOn) this._maxDbAddedOn = element.dbAddedOn;

        // Update indexes
        for (let key of this._indexes.keys()) {
            const fn = this._indexFunctions.get(key);
            if (!fn) {
                console.error(`No index function for ${key} in ArrayMap<${this._type}>`);
                continue;
            }

            const oldCheck = oldValueClone ? fn(oldValueClone) : false;
            const newCheck = fn(element);

            if (oldValueClone && oldCheck && !newCheck) {
                // Delete from index
                this._indexes.get(key)?.delete(oldValueClone.id);
            }

            else if (newCheck) {
                // Ensure that the new value is accepted in the index
                this._indexes.get(key)?.set(element.id, element);
            }
        }

        // Add to mappings (if value present and not deleted)
        if (element.status !== 'D') {
            // Start by getting the field names for each of the mappings
            for (let fieldName of this._mappings.keys()) {

                // Get the distinct key(s) present in this element
                const {keysFn} = this._mappingFunctions.get(fieldName) || {};
                if (!keysFn) {
                    console.error(`No keysFn for ${fieldName} in ArrayMap<${this._type}>`);
                    continue;
                }

                const mapping = this._mappings.get(fieldName);
                if (!mapping) {
                    console.error(`No mapping for ${fieldName} in ArrayMap<${this._type}>`);
                    continue;
                }

                // Delete this entry and let it be assigned anew in _updateMapping.
                for (let [, values] of mapping) {
                    // Value is a map!
                    values.delete(element.id);
                }

                // Now re-add the new value
                const distinctValues = keysFn(element);

                if (Array.isArray(distinctValues)) {
                    distinctValues.forEach(key => this._updateMapping(mapping, key, element));
                }
                else this._updateMapping(mapping, distinctValues, element);
            }
        }
    }

    /**
     * Map-like size attribute. Returns the number of elements in the Map.
     *
     * @returns {number}
     */
    get size(): number {
        return this._map.size;
    }

    /**
     * Map-like reduce function.
     *
     * @param {function} fn
     * @param {number} startingCondition
     * @returns {any}
     */
    reduce = (fn: (previousValue: any, currentValue: any, currentIndex: number, array: any[]) => any, startingCondition: number): any => {
        return this.values().reduce(fn, startingCondition);
    }

    /**
     * Return a sorted array.
     *
     * @param {function} fn
     * @return {<T>[]}
     */
    sort = (fn: ((a: T, b: T) => number) | undefined): T[] => {
        const result: T[] = [];

        this._map.forEach(o => {
            if (o) result.push(o as T);
        });

        return result.sort(fn);
    }

    /**
     * Get the type of element stored within, e.g. Task
     *
     * @returns {string}
     */
    get type(): string {
        return this._type;
    }

    /**
     * Set the type of elements stored within, e.g. Task
     *
     * @param {string} t
     */
    set type(t: string) {
        this._type = t;
    }

    /**
     * Array-like some method.
     *
     * @param {function} fn
     */
    some = (fn: (o: T) => boolean): boolean => {
        for (const o of this._map.values()) {
            if (fn(o)) return true;
        }
        return false;
    }

    /**
     * Map-like values method. Memoized to return the same array reference
     * if the underlying data hasn't changed.
     *
     * @returns {<T>[]}
     */
    values = (): T[] => {
        // Return cached array if available
        const cached = _valuesCache.get(this._map);
        if (cached) return cached as T[];

        // Build and cache the array
        const result: T[] = [];
        this._map.forEach(o => {
            if (o) result.push(o as T)
        });

        _valuesCache.set(this._map, result);
        return result;
    }

    get maxAddedOn() {
        return this._maxAddedOn;
    }

    get maxDbAddedOn() {
        return this._maxDbAddedOn;
    }
}

export default ArrayMap;
